import { Dayjs } from 'dayjs';
import { v4 as uuid } from 'uuid';

import { SummitItemControl } from '@summit/form';
import { SummitIdObject } from '@summit/models';

import { assignSafe } from '@summit/core';
import { EboObject } from '../base-classes/ebo-object';
import { Employee } from '../employee/employee';
import { PayType } from '../payroll/shared/pay-type.enum';
import { Hub } from '../places/hub/hub';
import { RouteRun } from '../route/route-run';
import { DispatchType, DispatchTypeArrow } from '../schedule/dispatch-type.enum';
import { ScheduleStatus } from '../schedule/schedule-status.enum';
import { SpotRun } from '../spot/spot-run';
import { TruckAlias } from '../truck/truck-alias';
import { DigitalLogType } from './digital-log-type.enum';

export class ScheduleLegBase extends EboObject {
  eventDate: Dayjs; //
  runCount: number; //
  legCount: number; //

  runId: string;
  run: RouteRun;
  dispatchType: DispatchType;

  itemType: DigitalLogType;
  payType: PayType;

  origHubID: number;
  origHub: Hub;

  destHubID: number;
  destHub: Hub;
  spotRunID: string;
  spotRun: SpotRun;

  driver1ID: string;
  driver1?: Employee;
  driver2ID: string;
  driver2?: Employee;

  truckAliasID: string; //
  truckAlias?: TruckAlias;

  status: ScheduleStatus; //

  estLayover: number;

  comment: string;

  static CleanupLeg<T extends SummitIdObject>(
    item: ScheduleLegBase,
    destControl: SummitItemControl<T, any>,
    spotControl: SummitItemControl<T, any>
  ): T {
    item.itemType = parseInt(item.itemType.toString());
    switch (item.itemType) {
      case DigitalLogType.FedExLinehaul: {
        item.destHub = destControl?.getObject();
        item.spotRunID = null;
        item.spotRun = null;
        break;
      }
      case DigitalLogType.FedExSpot: {
        item.spotRun = spotControl?.getObject();
        item.destHubID = null;
        item.destHub = null;
        break;
      }
      default:
        break;
    }
    return item as unknown as T;
  }

  toShortString(): string {
    return `${this.eventDate.format('ddd')} Run #${this.runCount} Leg #${
      this.legCount
    } ${this.origHub?.toShortString()}=>${this.destHub?.toShortString()}`;
  }

  constructor(jsonData: Partial<ScheduleLegBase>) {
    super();
    if (jsonData) {
      assignSafe(this, jsonData);
      if (jsonData.spotRun) this.spotRun = new SpotRun(jsonData.spotRun);
      if (jsonData.origHub) this.origHub = new Hub(jsonData.origHub);
      if (jsonData.destHub) this.destHub = new Hub(jsonData.destHub);
      if (jsonData.driver1) this.driver1 = new Employee(jsonData.driver1);
      if (jsonData.driver2) this.driver2 = new Employee(jsonData.driver2);
      if (jsonData.truckAlias) this.truckAlias = new TruckAlias(jsonData.truckAlias);
    }
    this.id ??= uuid();
    this.payType ??= PayType.Info;
    this.itemType ??= DigitalLogType.FedExLinehaul;
    this.dispatchType ??= DispatchType.Normal;
    //if (this.dispatchType != DispatchType.Normal) console.log('Dispatch Type', this.dispatchType);
  }

  get destName(): string | null {
    let response: string | null = null;
    switch (this.itemType) {
      case DigitalLogType.FedExLinehaul:
        response = this.destHub?.toShortString() ?? this.destHubID.toString();
        if (this.dispatchType != DispatchType.Normal) response = DispatchTypeArrow[this.dispatchType] + response;
        break;
      case DigitalLogType.FedExSpot: {
        //console.log('Spot Run', this.spotRun);
        response = this.spotRun?.toShortString() ?? this.spotRunID.toString();
        break;
      }
      case DigitalLogType.Any:
      default:
        response = null;
        break;
    }
    //console.log(`Dest Name for ${response}`, this.itemType);
    return response;
  }

  get origAbbr(): string {
    return this.origHubID == 0 ? Hub.AdminScheduleDisplayString : this.origHubID.toString();
  }

  get destAbbr(): string | null {
    //console.log('Dest Name for', this.itemType);
    switch (this.itemType) {
      case DigitalLogType.FedExLinehaul:
        if (this.destHubID == 0) return Hub.AdminScheduleDisplayString;
        return (this.destHubID ?? 0).toString();
      case DigitalLogType.FedExSpot: {
        //console.log('Spot Run', this.spotRun);
        return `#${this.spotRun?.spotNumber ?? 0}`;
      }
      case DigitalLogType.Any:
        return null;
      default:
        break;
    }
    return null;
  }
}
